import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Center,
  ThemeIcon,
  Box,
  useMantineTheme,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";
import { normalizeDob } from "../utils/validateDob";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../utils/edge";

const BasicDataCollectionForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  loadingText,
  errorText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputSize = useAdjustInputSize();
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const totalAttemptsUsedRef = useRef(0);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Check if edge mode is enabled
  const isEdgeMode = data?.edge === true;

  // Status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      return true;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (
      (currentAttempt === 0 || currentAttempt === 1) &&
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt
    ) {
      lastConfirmationRef.current = confirmationNumber;
      if (currentAttempt === 0) {
        setCurrentAttempt(1);
      }
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        try {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        } catch (err) {
          // Silent fail
        }
      }

      setLoading(true);
      return true;
    }

    try {
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
    } catch (err) {
      // Silent fail
    }

    setLoading(true);

    if (!result) {
      setResult(true);
    }

    return true;
  };

  // Status check hooks - use edge status check when in edge mode and polling is triggered
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    try {
      let apiUrl;
      let requestData = { ...data };

      if (isEdgeMode) {
        const edgeQueueUrl = getEdgeQueueUrl();
        apiUrl = edgeQueueUrl;
        requestData.queue = "idmatchplus";
      } else {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json();

      if (isEdgeMode) {
        // In edge mode, start polling for questions
        const reqId = responseData.request_id || data.request_id;
        if (reqId) {
          pollForQuestions(reqId, onApiResponse);
        } else {
          console.error("No request_id found in edge response");
          setIsVerifying(false);
        }
      } else {
        // Non-edge mode: handle synchronous response
        if (typeof onApiResponse === "function") {
          onApiResponse(responseData);
        }

        try {
          dispatch({ type: "SET_STATUS", payload: "PENDING" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
        } catch (err) {
          // Silent fail
        }

        setIsVerifying(false);
        setLoading(true);

        setCurrentAttempt((prevAttempt) => prevAttempt + 1);
        waitingForConfirmationChangeRef.current = true;
        setResult(true);
      }
    } catch (error) {
      alert(errorText || "Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  // Polling function for edge mode to get questions
  const pollForQuestions = async (requestId, onApiResponse) => {
    const maxAttempts = 100; // 5 minutes max
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const edgeRetrieveUrl = getEdgeRetrieveUrl();

        const response = await fetch(edgeRetrieveUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            request_id: requestId,
            type: "idmatchplus"
          }),
        });

        if (response.ok) {
          const result = await response.json();

          // Check if we got a final status with no questions - trigger edge status polling
          if (!result.has_questions && (result.status === "PASS" || result.status === "FAIL")) {
            // No questions returned, trigger edge status polling to get proper status
            setIsVerifying(false);
            setLoading(true);
            setEdgeStatusPolling(true);
            return; // Stop polling for questions, edge status check will handle the rest
          }

          // Check if we have questions
          if (result.has_questions && result.questions && result.questions.length > 0) {
            // Transform edge response to match expected format for handleApiResponse
            const transformedResponse = {
              idr5_response: {
                output: {
                  questions: {
                    questions: result.questions
                  }
                },
                continuations: {
                  questions: {
                    template: result.continuation_template
                  }
                }
              }
            };

            if (typeof onApiResponse === "function") {
              onApiResponse(transformedResponse);
            }

            setIsVerifying(false);
            setCurrentAttempt((prevAttempt) => prevAttempt + 1);
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No questions received after 5 minutes");
          setIsVerifying(false);
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsVerifying(false);
        }
      }
    };

    poll();
  };

  const handleDobChange = (dob) => {
    const formattedDob = normalizeDob(`${dob.year || ""}${dob.month || ""}${dob.day || ""}`);
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);

    const errors = form.validate();
    if (!errors.hasErrors) {
      if (!data.settings) data.settings = {};
      data.settings.currentAttempt = 0;
      data.settings.totalAttempts = reAttempts + 1;
      data.attemptsLeft = reAttempts;

      // For edge mode, we need to submit to the edge queue endpoint first
      if (isEdgeMode) {
        const autoSubmitEdge = async () => {
          setIsVerifying(true);
          setLoading(true);

          // Copy form values to data
          Object.keys(form.values).forEach((key) => {
            data[key] = form.values[key];
          });

          try {
            const edgeQueueUrl =
              process.env.REACT_APP_EDGE_QUEUE_URL ||
              "https://dcams.app/edge/frontend/api/queue/batch";

            const requestData = { ...data, queue: "idmatchplus" };

            const response = await fetch(edgeQueueUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestData),
            });

            const responseData = await response.json();
            const reqId = responseData.request_id || data.request_id;

            if (reqId) {
              pollForQuestions(reqId, onApiResponse);
            } else {
              console.error("No request_id found in edge response");
              setIsVerifying(false);
              setLoading(false);
            }
          } catch (error) {
            console.error("Edge auto-submit error:", error);
            setIsVerifying(false);
            setLoading(false);
          }
        };

        autoSubmitEdge();
      } else {
        // Non-edge mode: start status polling
        setLoading(true);
        setResult(true);
      }
    } else {
      setLoading(false);
      form.clearErrors();
    }
  }, [componentKey]);

  if (loading && !isVerifying) {
    return <LoadingOverlay visible />;
  }

  const groupedFields = config.fields.reduce(
    (acc, field) => {
      if (field.group) {
        if (!acc.grouped[field.group]) {
          acc.grouped[field.group] = [];
        }
        acc.grouped[field.group].push(field);
      } else {
        acc.ungrouped.push(field);
      }
      return acc;
    },
    { grouped: {}, ungrouped: [] },
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          height: "100%",
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box style={{ flex: 1, width: "100%" }}>
            <Stack spacing={8}>
              <Center>
                <ThemeIcon
                  variant="light"
                  c={
                    data.styles?.primaryColor
                      ? data.styles.primaryColor
                      : "teal"
                  }
                  size={inputSize === "md" ? 60 : 55}
                  radius="xl"
                >
                  {icon}
                </ThemeIcon>
              </Center>
              <Stack spacing={0} mb="md">
                <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                  {title}
                </Text>
                <Text c="dimmed" fz={inputSize}>
                  {subtitle}
                </Text>
              </Stack>
            </Stack>
            <Stack spacing="sm">
              {Object.keys(groupedFields.grouped).map((group) => (
                <MantineGroup key={group} spacing="sm" noWrap grow>
                  {groupedFields.grouped[group].map((field) => (
                    <FormField
                      key={field.name}
                      field={field}
                      form={form}
                      settings={settings}
                      componentKey={componentKey}
                      showMoreFields={true}
                      onDobChange={handleDobChange}
                      onAddressChange={handleAddressChange}
                      isSinglePageForm={true}
                    />
                  ))}
                </MantineGroup>
              ))}

              <Stack spacing="sm">
                {groupedFields.ungrouped.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    form={form}
                    settings={settings}
                    componentKey={componentKey}
                    showMoreFields={true}
                    onDobChange={handleDobChange}
                    onAddressChange={handleAddressChange}
                    isSinglePageForm={true}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box mt="md" style={{ width: "100%" }}>
            <Button
              fullWidth
              size="lg"
              type="submit"
              disabled={isVerifying || !form.isValid()}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.01)",
                },
                '&[data-loading="true"]': {
                  color: buttonContentColor,
                },
              }}
            >
              {isVerifying ? (
                <MantineGroup spacing="xs" noWrap>
                  <Loader size="xs" color={buttonContentColor} />
                  <Text c="inherit">{loadingText || "Getting Questions..."}</Text>
                </MantineGroup>
              ) : (
                buttonText
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default BasicDataCollectionForm;
